import { useEffect, useState } from 'react'
import { DateField, TimeField } from '@astro/date-field'
import { LocationSelect } from '@astro/kundli-studio'

/** Required fields the backend needs on the booking record — notes stays optional.
 * When birth time is marked unknown, the three photos substitute for it. */
export function isBirthDetailsValid(value) {
  if (!value?.birthDate || !value?.place?.trim()) return false
  if (value.birthTimeUnknown) {
    return Boolean(value.photo && value.leftPalmPhoto && value.rightPalmPhoto)
  }
  return Boolean(value.birthTime)
}

function useObjectUrl(file) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (!file) {
      setUrl(null)
      return undefined
    }
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])
  return url
}

function PhotoUploadRow({ label, hint, file, onChange }) {
  const previewUrl = useObjectUrl(file)
  return (
    <label className={`flex items-center gap-3 rounded-xl border-[1.5px] p-3 cursor-pointer transition-all bg-[#FAFAF9] hover:bg-[#FFF8EE] ${file ? 'border-green-500/45' : 'border-[#D7C29A]/40'}`}>
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {previewUrl ? (
        <img src={previewUrl} alt={`${label} preview`} className="w-11 h-11 rounded-lg object-cover border border-secondary/30 shrink-0" />
      ) : (
        <span className="w-11 h-11 rounded-lg border border-dashed border-[#D7C29A]/50 bg-secondary/5 text-secondary flex items-center justify-center text-base shrink-0">📷</span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-[#1C140A] truncate">{file ? file.name : label}</p>
        <p className="text-[10px] text-[#8B7355] mt-0.5 truncate">{file ? 'Tap to change' : hint}</p>
      </div>
    </label>
  )
}

/** Seeker's birth facts, collected at booking time — the backend stores these
 * on the booking itself (birth_date/birth_time/place) so the astrologer can
 * read them from the booking detail page. Shared by both booking flows.
 *
 * `allowUnknownBirthTime` (Maharaj flow only) lets the seeker skip the exact
 * birth time and instead upload a photo + both palms, which the astrologer
 * reads the chart from manually. */
export default function BirthDetailsForm({ value, onChange, allowUnknownBirthTime = false }) {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value })
  const setFile = (field) => (file) => onChange({ ...value, [field]: file })

  const toggleUnknownBirthTime = (e) => {
    const birthTimeUnknown = e.target.checked
    onChange({
      ...value,
      birthTimeUnknown,
      birthTime: birthTimeUnknown ? '' : value.birthTime,
      photo: birthTimeUnknown ? value.photo : null,
      leftPalmPhoto: birthTimeUnknown ? value.leftPalmPhoto : null,
      rightPalmPhoto: birthTimeUnknown ? value.rightPalmPhoto : null,
    })
  }

  return (
    <div className="info-card overflow-hidden">
      <div className="h-[3px] w-full bg-gradient-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
      <div className="p-6 border-b border-[#D7C29A]/20 bg-gradient-to-r from-[#FFFCF5] to-[#FEF6E4] flex items-center gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21c-4.5-3-7-6.5-7-10a7 7 0 0 1 14 0c0 3.5-2.5 7-7 10Z" />
            <circle cx="12" cy="11" r="2.5" />
          </svg>
        </span>
        <div>
          <span className="text-[10px] font-mono font-normal text-secondary uppercase tracking-[0.3em]">Birth Chart Details</span>
          <h4 className="text-base font-cinzel text-[#1C140A]">For an accurate reading</h4>
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-1.5 block">Date of birth</span>
            <DateField
              required
              value={value.birthDate}
              onChange={set('birthDate')}
              className="w-full rounded-lg border border-[#D7C29A]/40 px-3 py-2.5 text-sm text-[#1C140A] focus:outline-none focus:ring-2 focus:ring-secondary/40"
            />
          </label>
          {!value.birthTimeUnknown && (
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-1.5 block">Time of birth</span>
              <TimeField
                required
                value={value.birthTime}
                onChange={set('birthTime')}
                className="w-full rounded-lg border border-[#D7C29A]/40 px-3 py-2.5 text-sm text-[#1C140A] focus:outline-none focus:ring-2 focus:ring-secondary/40"
              />
            </label>
          )}
        </div>

        {allowUnknownBirthTime && (
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={Boolean(value.birthTimeUnknown)}
              onChange={toggleUnknownBirthTime}
              className="h-4 w-4 rounded border-[#D7C29A]/60 text-secondary focus:ring-secondary/40"
            />
            <span className="text-xs text-[#8B7355]">I don&rsquo;t know my exact birth time</span>
          </label>
        )}

        {allowUnknownBirthTime && value.birthTimeUnknown && (
          <div className="space-y-3 p-4 rounded-xl border border-dashed border-secondary/30 bg-secondary/5">
            <p className="text-[10px] text-[#8B7355] leading-relaxed">
              Without a birth time, the astrologer will read your chart from a photo of you and both palms instead.
            </p>
            <PhotoUploadRow
              label="Your photo"
              hint="A clear, front-facing photo"
              file={value.photo}
              onChange={setFile('photo')}
            />
            <PhotoUploadRow
              label="Left palm photo"
              hint="Flat, well-lit, palm facing up"
              file={value.leftPalmPhoto}
              onChange={setFile('leftPalmPhoto')}
            />
            <PhotoUploadRow
              label="Right palm photo"
              hint="Flat, well-lit, palm facing up"
              file={value.rightPalmPhoto}
              onChange={setFile('rightPalmPhoto')}
            />
          </div>
        )}

        <div>
          <label htmlFor="booking-birth-place" className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-1.5 block">Place of birth</label>
          {/* POST /bookings takes `place` as a plain string, so the widget's
            * geocoded pick is mirrored into `place` as its canonical Name —
            * the seeker must choose a real suggestion, free text never
            * reaches the API. Coords ride on `placeLocation` (client-only). */}
          <LocationSelect
            inputId="booking-birth-place"
            value={value.placeLocation ?? null}
            onChange={(loc) => onChange({ ...value, placeLocation: loc, place: loc?.Name || '' })}
            placeholder="Search your birth city…"
          />
        </div>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-1.5 block">Notes for the astrologer (optional)</span>
          <textarea
            rows={3}
            placeholder="Anything specific you'd like addressed in the session…"
            value={value.notes}
            onChange={set('notes')}
            className="w-full rounded-lg border border-[#D7C29A]/40 px-3 py-2.5 text-sm text-[#1C140A] placeholder:text-[#8B7355]/50 focus:outline-none focus:ring-2 focus:ring-secondary/40 resize-none"
          />
        </label>
      </div>
    </div>
  )
}
